import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// API
import { getAdminAuditions } from "../../services/adminApi";

// Reusable Table Component
import ReusableAdminTable from "./ReusableAdminTable";

// UI
import { Button, Badge } from "react-bootstrap";
import { FiEye } from "react-icons/fi";

const AdminAuditionsList = () => {
    // ================================
    // STATES
    // ================================
    const [auditions, setAuditions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const navigate = useNavigate();

    // ================================
    // LOAD DATA
    // ================================
    const loadAuditions = useCallback(async () => {
        setIsLoading(true);
        setError("");

        try {
            const res = await getAdminAuditions();

            const rawData = res?.data?.data || [];

            const mappedData = rawData.map((item) => ({
                id: item.roleApplicationId,

                roleId: item.roleId,
                applicationId: item.applicationId,
                roleApplicationId: item.roleApplicationId,

                candidateName: item.candidateName,
                castingDirector: item.castingDirector,
                roleName: item.roleName,

                auditionType: item.auditionType,
                paymentStatus: item.paymentStatus,
                paymentId: item.paymentId,

                auditionDate: item.auditionDate,
                auditionMonth: item.auditionMonth,

                verifyStatus: item.verifyStatus,
                status: item.status,
            }));

            setAuditions(mappedData);
        } catch (err) {
            console.error("Error loading auditions:", err);
            setError("Failed to load auditions.");
            setAuditions([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // ================================
    // INITIAL LOAD
    // ================================
    useEffect(() => {
        loadAuditions();
    }, [loadAuditions]);

    // ================================
    // VIEW APPLICATION
    // ================================
    const handleViewApplication = (roleApplicationId) => {
        if (!roleApplicationId) {
            alert("Application ID not found.");
            return;
        }

        navigate(`/admin/manage-applications/${roleApplicationId}`);
    };

    // ================================
    // TABLE COLUMNS
    // ================================
    const columns = useMemo(
        () => [
            {
                accessorKey: "id",
                header: "#",
                cell: (info) => info.row.index + 1,
            },

            {
                accessorKey: "roleId",
                header: "Role ID",
            },

            {
                accessorKey: "applicationId",
                header: "Application ID",
            },

            {
                accessorKey: "candidateName",
                header: "Candidate",
            },

            {
                accessorKey: "castingDirector",
                header: "Casting Director",
            },

            {
                accessorKey: "roleName",
                header: "Role Name",
            },

            {
                accessorKey: "auditionType",
                header: "Audition Type",
                cell: (info) => (
                    <Badge bg="info">
                        {info.getValue() || "N/A"}
                    </Badge>
                ),
            },

            {
                accessorKey: "paymentStatus",
                header: "Payment",
                cell: (info) => {
                    const status = info.getValue();

                    return (
                        <Badge
                            bg={
                                status === "BOOKED"
                                    ? "success"
                                    : status === "PENDING"
                                    ? "warning"
                                    : "secondary"
                            }
                        >
                            {status || "N/A"}
                        </Badge>
                    );
                },
            },

            {
                accessorKey: "paymentId",
                header: "Payment ID",
            },

            {
                accessorKey: "auditionDate",
                header: "Audition Date",
            },

            {
                accessorKey: "auditionMonth",
                header: "Month",
            },

            {
                accessorKey: "verifyStatus",
                header: "Verify Status",
                cell: (info) => {
                    const status = info.getValue();

                    return (
                        <Badge
                            bg={
                                status === "VERIFIED"
                                    ? "success"
                                    : "warning"
                            }
                        >
                            {status || "Pending"}
                        </Badge>
                    );
                },
            },

            {
                accessorKey: "status",
                header: "Status",
                cell: (info) => {
                    const status = info.getValue();

                    return (
                        <Badge
                            bg={
                                status === "ACTIVE"
                                    ? "success"
                                    : "secondary"
                            }
                        >
                            {status || "N/A"}
                        </Badge>
                    );
                },
            },

            {
                id: "actions",
                header: "Actions",
                cell: (info) => (
                    <div className="d-flex justify-content-center gap-2">
                        <Button
                            variant="link"
                            className="p-0 text-info"
                            onClick={() =>
                                handleViewApplication(
                                    info.row.original.roleApplicationId
                                )
                            }
                        >
                            <FiEye size={18} />
                        </Button>
                    </div>
                ),
            },
        ],
        []
    );

    // ================================
    // RENDER
    // ================================
    return (
        <ReusableAdminTable
            data={auditions}
            columns={columns}
            isLoading={isLoading}
            title="Auditions Management"
            searchPlaceholder="Search auditions..."
            exportCsvFilename="auditions-list.csv"
        />
    );
};

export default AdminAuditionsList;